<template>
  <div v-loading="loadingScope.loading">
    <el-upload
      v-model:file-list="fileList"
      class="avatar-uploader"
      :class="{'avatar-uploader--disabled': !actionUpload}"
      list-type="picture-card"
      :limit="limit"
      multiple
      :action="uploadUrl"
      :accept="accept"
      :disabled="loadingScope.loading || disabled"
      :before-upload="beforeUpload"
      :http-request="uploadMethod"
      :on-preview="handlePreview"
      :on-remove="handleRemove"
      :on-exceed="handleExceed"
    >
      <el-icon
        v-if="actionUpload"
        class="avatar-uploader-icon"><Plus /></el-icon>
    </el-upload>
  </div>
</template>

<script setup>
import { computed, reactive, ref, watchEffect, defineProps, defineEmits, defineExpose } from 'vue'
import { commonUtils, vab } from '@libs/utils'
// import { getFileBase64, getFileType } from '@/utils/file'
import fileComp from '@/compositions/modules/file'
import { uploadFile, getFilePath, growUploadFile, growGetFileInfo  } from '@/api/modules/common'

import {
  imgType,
  pdfType,
  docsType,
  docType,
  pptType,
  xlsType,
  txtType,
  compressType,
  videoType
} from '@/config'

const props = defineProps({
  acctId: String,
  fileTag: String,
  fileIdList: Array,
  filePathType: String,
  accept: String,
  limit: Number,
  initialFileList: Array,
  disabled: Boolean,
  filePrefix: String,
  growUrl: {
    type: Boolean,
    default: false
  }
})

const emits = defineEmits(['upload', 'update:fileIdList', 'uploading'])
const { getFileType, fileToBase64, getFileRemoteInfo } = fileComp()

const uploadUrl = `${process.env.VUE_APP_BASE_URL}/mgmt/v1/oss/upload`
const accept = ref('')
const fileList = ref([])

const actionUpload = computed(() => {
  return commonUtils.isEmpty(props.limit) || fileList.value.length < props.limit
})

const fileAcceptMap = {
  contractFileAccept: [...imgType, ...pdfType],
  invoiceFileAccept: [...imgType, ...pdfType],
  logisticsFileAccept: [...imgType, ...pdfType],
  communicationRecordFileAccept: [...imgType, ...pdfType, ...docsType],
  otherFileAccept: [...imgType, ...pdfType, ...docsType, ...txtType, ...compressType, ...videoType],
  eddAccept: [...imgType, ...pdfType]
}

const fileTagMap = {
  contractFile: '1',
  invoiceFile: '2',
  logisticsFile: '3',
  communicationRecordFile: '4',
  otherFile: '5',
  edd: '7'
}

const beforeUpload = (file) => {
  console.log(getFileType(file))
  console.log(accept.value.includes(getFileType(file)))
  if (!accept.value.includes(getFileType(file))) {
    vab.baseMessage('文件类型错误，请重新选择', 'error')

    return false
  }
  if ((file.size / 1024 / 1024) >= 15) {
    vab.baseMessage('文件不能超过15MB', 'error')

    return false
  }
}

const loadingScope = reactive({
  loading: false
})
const uploadMethod = async (options) => {
  try {
    const base64Str = await fileToBase64(options.file)

    const param = {
      fileBase64: base64Str.replace(/^data:(.*?);base64,/, ''),
      fileType: 'KY',
      filePrefix: props.filePrefix,
      srcFileName: options.file.name,
      acctId: props.acctId
    }
    emits('uploading', { status: 'uploading' })
    const { fileId, fileName } = props.growUrl ? await growUploadFile(param) : await uploadFile(param)
    // loadingScope.loading = loading
    // const { fileId, fileName } = await execute()
    vab.baseMessage('上传成功', 'success')
    const target = fileList.value.find(item => item.uid === options.file.uid)
    target.fileId = fileId
    target.fileName = fileName
    target.fileUrl = target.url
    target.url = checkFileUrl(fileName, target.url)
    emits('update:fileIdList', fileList.value.map(item => item.fileId))
    emits('upload', fileList.value)
    emits('uploading', { status: 'done' })
  } catch (e) {
    emits('uploading', { status: 'error' })
  }
}
const checkFileUrl = (fileName, fileUrl) => {
  const fileType = fileName.split('.').reverse()[0]
  // 图片
  if (imgType.includes(`.${fileType}`)) {
    return fileUrl
  }
  if (compressType.includes(`.${fileType}`)) {
    return require('../assets/img/filePreview/zip.png')
  }
  if (pdfType.includes(`.${fileType}`)) {
    return require('../assets/img/filePreview/pdf.png')
  }
  if (txtType.includes(`.${fileType}`)) {
    return require('../assets/img/filePreview/txt.png')
  }
  if (videoType.includes(`.${fileType}`)) {
    return require('../assets/img/filePreview/video.png')
  }
  if (pptType.includes(`.${fileType}`)) {
    return require('../assets/img/filePreview/ppt.png')
  }
  if (xlsType.includes(`.${fileType}`)) {
    return require('../assets/img/filePreview/xls.png')
  }
  if (docType.includes(`.${fileType}`)) {
    return require('../assets/img/filePreview/doc.png')
  }
  return fileUrl
}
const handlePreview = (file) => {
  if (file.fileUrl) {
    window.open(file.fileUrl)
  }
}
const handleRemove = (file) => {
  emits('update:fileIdList', fileList.value.map(item => item.fileId))
  emits('remove', fileList.value)
}
const handleExceed = (file) => {
  if (!props.limit) return
  vab.baseMessage(`最多上传${props.limit}个文件`, 'error')
}
const getFileUrlById = async (fileId) => {
  if (!fileId) return null
  const param = {
    fileId,
    acctId: props.acctId
  }
  const { fileUrl, fileName } = props.growUrl ? await growGetFileInfo(param) : await getFilePath(param)
  return {
    url: checkFileUrl(fileName) || fileUrl,
    fileUrl,
    fileId
  }
}
const getAllFileUrlById = async (fileIdList) => {
  const fileUrlList = await Promise.all(fileIdList.map(item => (getFileUrlById(item))))
  fileList.value = fileUrlList
}
watchEffect(() => {
  accept.value = props.fileTag ? fileAcceptMap[`${props.fileTag}Accept`].join(',') : props.accept
  if (commonUtils.isEmpty(props.fileIdList)) {
    fileList.value = []
  }
})
watchEffect(() => {
  const initialFileList = (props.initialFileList || []).filter(Boolean)
  console.log(initialFileList, 'initialFileList====')
  if (initialFileList && initialFileList.length) {
    getAllFileUrlById(initialFileList)
  }
})
defineExpose({
  loadingScope
})
</script>

<style scoped lang="scss">
:deep(.avatar-uploader .el-upload) {
  width: 80px;
  height: 80px;
  border: 1px dashed var(--el-border-color);
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: var(--el-transition-duration-fast);
}
:deep(.avatar-uploader .el-upload-list:not(.is-disabled) .avatar-uploader .el-upload:hover) {
  border-color: var(--el-color-primary);
}
:deep(.avatar-uploader .el-upload-list.is-disabled){
   & .el-upload{
     cursor: not-allowed;
     background-color: var(--el-color-info-light-9);
     color: var(--el-color-info-light-5);
     border-color: var(--el-color-info-light-8);
   }
}
:deep(.el-icon.avatar-uploader-icon) {
  font-size: 28px;
  color: #8c939d;
  width: 80px;
  height: 80px;
  text-align: center;
}
:deep(.avatar-uploader--disabled .el-upload.el-upload--picture-card) {
  display: none;
  pointer-events: none;
}
:deep(.el-upload-list--picture-card .el-upload-list__item-thumbnail) {
  object-fit: fill; // 图片撑满整个框
}
:deep(.el-icon--close-tip) {
  display: none !important;
}
:deep(.el-upload-list--picture-card .el-upload-list__item){
width: 80px;
height: 80px;
}
</style>
